// {{WORKTREE_ROOT}}/picosponsorconsole/packages/design/src/tailwind-preset.ts
// PLAN2 WP09, PLAN6 WP6-02 (radii, shadows, display font, success/cta colours) — C-95 / L-04: REPLACES Tailwind's palette, spacing, font-size, line-height, tracking, radius and
// border-width scales. Spacing keys are px with Tailwind's numbering (key = px / 4: p-4 = 16px, p-2.25 = 9px,
// w-105 = 420px) so every canvas value has a named utility and nothing scales with the root font size. Font sizes
// are px keys without line-height (text-13 = 13px). Colours are token names only. Preflight is off (tokens.css is the
// base layer). No `dark:` variants are used anywhere: tokens swap per mode (test/guards/no-dark-variant.test.ts).
import type { Config } from "tailwindcss";
import plugin from "tailwindcss/plugin.js";

const v = (name: string) => `var(--${name})`;

function buildSpacing(): Record<string, string> {
  const s: Record<string, string> = { 0: "0px", px: "1px" };
  for (let n = 1; n <= 40; n += 1) s[String(n / 4)] = `${n}px`;      // every px 1…40 (0.25 … 10)
  for (let n = 42; n <= 400; n += 2) s[String(n / 4)] = `${n}px`;    // every 2 px 42…400 (10.5 … 100)
  for (let n = 404; n <= 1600; n += 4) s[String(n / 4)] = `${n}px`;  // every 4 px 404…1600 (101 … 400)
  return s;
}
export const spacingPx: Record<string, string> = buildSpacing();

/** Every font size the canvas uses (digest §2.6) — px, no line-height. */
export const FONT_SIZES_PX = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 44, 46, 52, 56, 62, 64, 66, 72, 80, 82, 88, 100, 112, 124, 190] as const;
export const fontSizePx: Record<string, string> = Object.fromEntries(FONT_SIZES_PX.map((n) => [String(n), `${n}px`]));

/** The canvas's line-heights, unitless, keyed by value (leading-0.92). */
export const LINE_HEIGHTS = [0.76, 0.8, 0.82, 0.84, 0.85, 0.86, 0.88, 0.9, 0.92, 0.95, 0.96, 0.98, 1, 1.05, 1.1, 1.15, 1.2, 1.25, 1.3, 1.4, 1.45, 1.5, 1.55, 1.6, 1.7, 1.8, 1.9, 2] as const;
export const lineHeightScale: Record<string, string> = Object.fromEntries(LINE_HEIGHTS.map((n) => [String(n), String(n)]));

/** Named tracking (house convention): tight-N = −N/1000 em (tight-35 = −0.035em); wide-N = +N/100 em (wide-14 = 0.14em). */
export const letterSpacingEm: Record<string, string> = {
  "tight-70": "-0.07em", "tight-60": "-0.06em", "tight-55": "-0.055em", "tight-50": "-0.05em", "tight-45": "-0.045em",
  "tight-40": "-0.04em", "tight-35": "-0.035em", "tight-30": "-0.03em", "tight-25": "-0.025em", "tight-20": "-0.02em",
  "tight-15": "-0.015em", "tight-10": "-0.01em", normal: "0em",
  "wide-2": "0.02em", "wide-4": "0.04em", "wide-5": "0.05em", "wide-6": "0.06em", "wide-8": "0.08em", "wide-10": "0.1em",
  "wide-12": "0.12em", "wide-14": "0.14em", "wide-16": "0.16em", "wide-18": "0.18em", "wide-20": "0.2em",
};

/** w-pct-0 … w-pct-100 (pctClass). Safelisted because the class name is built at run time. */
export const pctWidths: Record<string, string> = Object.fromEntries(Array.from({ length: 101 }, (_, i) => [`pct-${i}`, `${i}%`]));

export const colors = {
  transparent: "transparent",
  current: "currentColor",
  inherit: "inherit",
  bg: { DEFAULT: v("bg"), 2: v("bg-2") },
  surface: { DEFAULT: v("surface"), 2: v("surface-2") },
  line: { DEFAULT: v("line"), strong: v("line-strong") },
  "control-line": v("control-line"),
  ink: v("ink"),
  dim: v("dim"),
  faint: v("faint"),
  accent: { DEFAULT: v("accent"), 2: v("accent-2"), wash: v("accent-wash"), tint: v("accent-tint"), "on-dark": v("accent-on-dark") },
  "on-accent": { DEFAULT: v("on-accent"), dark: v("on-accent-dark"), band: v("on-accent-band") },
  success: { DEFAULT: v("success"), 2: v("success-2"), wash: v("success-wash"), tint: v("success-tint"), "on-dark": v("success-on-dark") },
  "on-success": v("on-success"),
  cta: { DEFAULT: v("cta"), hover: v("cta-hover"), "on-dark": v("cta-on-dark") },
  "on-cta": { DEFAULT: v("on-cta"), dark: v("on-cta-dark") },
  "grade-verified": { bg: v("grade-verified-bg"), fg: v("grade-verified-fg"), ink: v("grade-verified-ink") },
  "kept-ink": v("kept-ink"),
  ochre: { DEFAULT: v("ochre"), wash: v("ochre-wash") },
  "on-ochre": v("on-ochre"),
  red: { DEFAULT: v("red"), wash: v("red-wash") },
  future: v("future"),
  // ── PLAN11 (D11-24) ── the logo red (graphics, eyebrows, badges; never a button), its text twin, the two chip washes and the light document.
  brand: { DEFAULT: v("brand"), text: v("brand-text") },
  "on-brand": v("on-brand"),
  info: { wash: v("info-wash") },
  muted: { wash: v("muted-wash") },
  paper: { DEFAULT: v("paper"), ink: v("paper-ink") },
  band: {
    bg: v("band-bg"), surface: v("band-surface"), ink: v("band-ink"), dim: v("band-dim"), faint: v("band-faint"),
    line: v("band-line"), hover: v("band-hover"), badge: v("band-badge"), red: v("band-red"),
  },
  "on-dark-body": v("on-dark-body"),
  capture: v("capture-bg"),
  "captured-chip": { DEFAULT: v("captured-chip-bg"), fg: v("captured-chip-fg") },
  scrim: v("scrim"),
  focus: v("focus-ink"),
} as const;

/** Class-name suffixes of every colour (bg-2, accent-wash, on-accent-dark, …) — cx() and the preset test use it. */
export const COLOR_NAMES: string[] = Object.entries(colors).flatMap(([k, val]) =>
  typeof val === "string" ? [k] : Object.keys(val).map((sub) => (sub === "DEFAULT" ? k : `${k}-${sub}`)),
);

/** D11-3: container-query variants for everything that renders inside a panel (a panel is narrower than the viewport). No new dependency. */
export const containerPlugin = plugin(({ addVariant, addUtilities }) => {
  addVariant("cq-md", "@container (min-width: 768px)");
  addVariant("cq-lg", "@container (min-width: 1024px)");
  addVariant("cq-xl", "@container (min-width: 1280px)");
  addUtilities({ ".cq": { "container-type": "inline-size" } });
});

export const picosponsorPreset: Partial<Config> = {
  darkMode: ["selector", '[data-theme="dark"]'],
  corePlugins: { preflight: false },
  plugins: [containerPlugin],
  safelist: [{ pattern: /^w-pct-(\d|[1-9]\d|100)$/ }],
  theme: {
    screens: { md: "768px", lg: "1024px", xl: "1280px" },
    colors,
    spacing: spacingPx,
    fontSize: fontSizePx,
    lineHeight: lineHeightScale,
    letterSpacing: letterSpacingEm,
    fontFamily: { sans: [v("font-sans")], display: [v("font-display")], mono: [v("font-mono")] },
    // PLAN6 §3.3: pills and 12–40 px corners; stock rounded-md/lg do not exist (built-css.test.ts).
    borderRadius: {
      none: "0px", 12: v("radius-12"), 14: v("radius-14"), 18: v("radius-18"), 24: v("radius-24"), 28: v("radius-28"),
      32: v("radius-32"), 40: v("radius-40"), round: v("radius-round"),
    },
    borderWidth: { DEFAULT: "1px", 0: "0px", 1: "1px", "1.5": "1.5px", 2: "2px", 3: "3px", 4: "4px", 6: "6px" },
    boxShadow: { none: "none", hero: v("shadow-hero"), "card-hover": v("shadow-card-hover"), spotlight: v("shadow-spotlight") },
    backgroundImage: { none: "none", hatch: v("hatch"), "hatch-soft": v("hatch-soft"), "hatch-dark": v("hatch-dark"), "page-glow": v("page-glow"), "band-glow": v("band-glow"), "auth-ground": v("auth-ground"), "auth-ground-band": v("auth-ground-band") },
    animation: { none: "none", pulse: v("motion-pulse"), "sweep-in": v("motion-sweep-in") },
    transitionDuration: { DEFAULT: v("motion-fast-ms"), 0: "0ms", fast: v("motion-fast-ms"), sweep: v("motion-sweep-ms") },
    transitionTimingFunction: { DEFAULT: "ease-out", linear: "linear", out: "ease-out" },
    extend: {
      width: { ...pctWidths, rail: v("rail-width"), "rail-collapsed": v("rail-collapsed"), agent: v("agent-width"), "agent-sm": v("agent-width-sm"), "agent-strip": v("agent-strip") },
      minHeight: { target: v("target-min"), "target-primary": v("target-primary"), "target-capture": v("target-capture") },
      minWidth: { target: v("target-min"), "target-primary": v("target-primary"), "target-capture": v("target-capture") },
      maxWidth: { console: v("console-max"), site: v("site-max") },
      zIndex: { 1: "1", 2: "2", 3: "3" },
    },
  },
};

export default picosponsorPreset;
